import { useEffect, useRef } from "react";
import { useSocketContext } from "../context/SocketContext";

/**
 * Custom hook for Socket.io events
 * @param {String} eventName - Event name to listen to
 * @param {Function} callback - Callback function when event is received
 * @param {Array} dependencies - Dependencies array for useEffect
 */
export const useSocket = (eventName, callback, dependencies = []) => {
  const { socket, isConnected } = useSocketContext();
  const callbackRef = useRef(callback);

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!socket) {
      console.log(`🔌 useSocket: No socket available for event: ${eventName}`);
      return;
    }

    if (!isConnected) {
      console.log(`🔌 useSocket: Socket not connected yet for event: ${eventName}. Waiting...`);
      // Still subscribe even if not connected - socket.io will queue events
    }

    console.log(`🔌 useSocket: Subscribing to event: ${eventName} (connected: ${isConnected})`);

    // Event handler wrapper
    const eventHandler = (...args) => {
      console.log(`📨 useSocket: Event received - ${eventName}:`, args);
      try {
        callbackRef.current(...args);
      } catch (error) {
        console.error(`❌ useSocket: Error in callback for ${eventName}:`, error);
      }
    };

    // Subscribe to event
    socket.on(eventName, eventHandler);

    // Cleanup: unsubscribe on unmount or dependency change
    return () => {
      console.log(`🔌 useSocket: Unsubscribing from event: ${eventName}`);
      if (socket) {
        socket.off(eventName, eventHandler);
      }
    };
  }, [socket, isConnected, eventName, ...dependencies]);
};

/**
 * Hook to emit socket events
 */
export const useSocketEmit = () => {
  const { socket, isConnected } = useSocketContext();

  const emit = (eventName, data) => {
    if (socket && isConnected) {
      socket.emit(eventName, data);
    } else {
      console.warn("Socket not connected. Cannot emit event:", eventName);
    }
  };

  return { emit, isConnected };
};

export default useSocket;

